import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTableSearchParams } from '@/hooks/useTableSearchParams';
import { harnessService } from '@/services/api/harnessService';

/**
 * Browses past Batches (Story 3.1, AC1): a filtered, paged list modeled on
 * `features/tests/TestsPage`. Filter state (device identity + date range + free-text search) and
 * pagination live in URL params via {@link useTableSearchParams}, so a filtered view is
 * shareable/back-navigable. Opening a row routes to the connection-free review view — nothing on
 * this path touches a device (AC4).
 */
export function BatchBrowser() {
  const navigate = useNavigate();
  const { page, pageSize, setPage, setPageSize, getParam, setParams } = useTableSearchParams();

  const search = getParam('search') ?? '';
  const deviceUid = getParam('deviceUid') ?? '';
  const serialNumber = getParam('serialNumber') ?? '';
  const from = getParam('from') ?? '';
  const to = getParam('to') ?? '';

  const { data, isLoading, error } = useQuery({
    // page is 0-based in the URL/table; the endpoint is 1-based.
    queryKey: ['harness-batches', page, pageSize, search, deviceUid, serialNumber, from, to],
    queryFn: () =>
      harnessService.listBatches({
        page: page + 1,
        pageSize,
        ...(search ? { search } : {}),
        ...(deviceUid ? { deviceUid } : {}),
        ...(serialNumber ? { serialNumber } : {}),
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      }),
  });

  const batches = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto', py: 4 }}>
      <Typography variant="h5" gutterBottom>
        Past Batches
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <TextField
          label="Search"
          size="small"
          value={search}
          onChange={(e) => setParams({ search: e.target.value || null })}
          placeholder="UID, serial, version, or ID"
        />
        <TextField
          label="Device UID"
          size="small"
          value={deviceUid}
          onChange={(e) => setParams({ deviceUid: e.target.value || null })}
        />
        <TextField
          label="Serial Number"
          size="small"
          value={serialNumber}
          onChange={(e) => setParams({ serialNumber: e.target.value || null })}
        />
        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setParams({ from: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setParams({ to: e.target.value || null })}
          InputLabelProps={{ shrink: true }}
        />
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load Batches. Please try again.</Alert>}

      {!isLoading && !error && (
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Device UID</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Firmware</TableCell>
                  <TableCell>Hardware</TableCell>
                  <TableCell align="right">Measurements</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {batches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No Batches match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  batches.map((b) => (
                    <TableRow
                      key={b.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/harness/batches/${b.id}`)}
                    >
                      <TableCell
                        sx={{
                          maxWidth: 240,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={b.name?.trim() || undefined}
                      >
                        {b.name?.trim() || '—'}
                      </TableCell>
                      <TableCell>{new Date(b.createdAt).toLocaleString()}</TableCell>
                      <TableCell>{b.deviceUid ?? '—'}</TableCell>
                      <TableCell>{b.serialNumber ?? '—'}</TableCell>
                      <TableCell>{b.firmwareVersion ?? '—'}</TableCell>
                      <TableCell>{b.hardwareVersion ?? '—'}</TableCell>
                      <TableCell align="right">{b.measurementCount}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
          />
        </Paper>
      )}
    </Box>
  );
}
